import { useAuthStore } from '@/stores/authStore'

/** Role name that is granted every permission by the backend seed data. */
export const SUPER_ADMIN_ROLE = 'super_admin'

/**
 * Hook for checking RBAC permissions of the current admin user.
 *
 * Permissions are issued by the API at login (and after the 2FA challenge) and
 * kept in the auth store. Two cases deliberately grant access:
 *
 *  1. **super_admin** — the backend seeds this role with every permission
 *     (`api/migrations/055_rbac.sql`), so a stale or partial permission list
 *     must never lock a super admin out of their own panel.
 *  2. **No permission list at all** — the API's `permissions` field is
 *     `omitempty`, so an admin whose `admin_role_id` is NULL receives no field
 *     rather than an empty one. That is "the server didn't tell us", not "the
 *     server said no", and treating it as denial would blank the whole panel
 *     for legacy accounts. An explicitly present list — including `[]` — does
 *     restrict.
 *
 * This is a UX layer only. The API authorises every request independently, so
 * being permissive here cannot grant real access; being wrong in the other
 * direction would make the panel unusable.
 */
export function usePermissions() {
  const user = useAuthStore((s) => s.user)
  const rawPermissions = user?.permissions
  const permissions = rawPermissions ?? []
  const role = user?.role_name ?? user?.role ?? ''

  const isSuperAdmin = role === SUPER_ADMIN_ROLE || user?.role === SUPER_ADMIN_ROLE
  const unrestricted = isSuperAdmin || rawPermissions === undefined

  return {
    has: (p: string) => unrestricted || permissions.includes(p),
    hasAny: (...ps: string[]) => unrestricted || ps.some((p) => permissions.includes(p)),
    hasAll: (...ps: string[]) => unrestricted || ps.every((p) => permissions.includes(p)),
    role,
    isSuperAdmin,
    permissions,
  }
}
